import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Lock,
  MessageSquare,
  Pin,
  Plus,
  Trash2,
  X,
  History,
  RefreshCw,
} from "lucide-react";
import DesignDetails from "./components/DesignDetails";
import DesignMemberSelector from "./components/DesignMemberSelector";
import DesignTaskLinkSelector from "./components/DesignTaskLinkSelector";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import {
  approveDesignById,
  createCommentForDesign,
  createPinForDesign,
  deleteCommentById,
  deleteDesignById,
  deletePinById,
  downloadDiagram,
  fetchCommentsForDesign,
  fetchDesignMembers,
  fetchDesignVersions,
  fetchOrganizationMembers,
  fetchPinsForDesign,
  getDesignById,
  getStatusBadgeClass,
  lockDesignById,
  reopenDesign,
  replaceDesignFile,
  resolvePinById,
  updatePinById,
  getDesignApsStatus,
  fetchLinkedTasks,
} from "../../../api/design/service";
import {
  normalizeFileUrl,
  displayNameFromOrgMember,
  useLoadAssigneesAndApprovers,
  mapAssigneeIdToName,
} from "./DesignShared";
import ConfirmActionAlert from "components/ConfirmActionAlert";

// --- APSViewer Component ---

function useLoadCommentsAndPins({ drawing, onUpdate, refreshTick = 0 }) {
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      const list = await fetchCommentsForDesign(drawing.id);
      if (!mounted) return;
      const mapped = (list || []).map((c, idx) => ({
        id: c.id || c.pk || `${drawing.id}-c-${idx}`,
        text: c.comment || c.text || "",
        author: c.created_by || "Unknown",
        pin_linked: c.pin || null,
        createdAt: c.created_on || new Date().toISOString(),
        isResolved: !!c.is_resolved || !!c.isResolved || false,
      }));
      onUpdateRef.current?.((prev) => ({
        ...(prev || drawing),
        comments: mapped,
      }));
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      const list = await fetchPinsForDesign(drawing.id);
      if (!mounted) return;
      const mapped = (list || []).map((p, idx) => ({
        ...p,
        id: p.id || p.pk || `${drawing.id}-p-${idx}`,
        x:
          typeof p.x_coordinate !== "undefined"
            ? Number(p.x_coordinate)
            : typeof p.x !== "undefined"
              ? Number(p.x)
              : 0,
        y:
          typeof p.y_coordinate !== "undefined"
            ? Number(p.y_coordinate)
            : typeof p.y !== "undefined"
              ? Number(p.y)
              : 0,
        z:
          typeof p.z_coordinate !== "undefined"
            ? Number(p.z_coordinate)
            : typeof p.z !== "undefined"
              ? Number(p.z)
              : undefined,
        comment: p.comment || p.text || "",
        status: p.status || "pending",
        assignees: p.assignees || [],
        author: p.created_by || "Unknown",
        createdAt: p.created_at || new Date().toISOString(),
      }));
      onUpdateRef.current?.((prev) => ({ ...(prev || drawing), pins: mapped }));
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);
}

// --- APSViewer Component ---

export const APSViewer = ({
  drawing,
  onBack,
  onUpdate,
  onDelete,
  currentMember,
}) => {
  const urn = drawing?.urn;
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { addAlert } = useAlert();
  useLoadCommentsAndPins({ drawing, onUpdate, refreshTick });
  const {
    orgMembers,
    orgMembersLoading,
    designMembers,
    designMembersLoading,
    refreshDesignMembers,
  } = useLoadAssigneesAndApprovers({ drawing, onUpdate });

  // UI States
  const [isPinMode, setIsPinMode] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [showPendingIssuesModal, setShowPendingIssuesModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);

  const [isReplacing, setIsReplacing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [showLinkToTask, setShowLinkToTask] = useState(false);
  const [linkedTasks, setLinkedTasks] = useState([]);
  // Pin States
  const [activePinId, setActivePinId] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinComment, setPinComment] = useState("");
  const [pinAssignees, setPinAssignees] = useState([]);
  const [isCreatingPin, setIsCreatingPin] = useState(false);

  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvePinId, setResolvePinId] = useState(null);
  const [resolveComment, setResolveComment] = useState("");

  // Sticky Pins State
  const [pinsWithModelCoords, setPinsWithModelCoords] = useState([]);
  const pinsWithModelCoordsRef = useRef([]);
  const [pinsWithScreenPos, setPinsWithScreenPos] = useState([]);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const isLocked = drawing.status === "Locked";
  const isLockedRef = useRef(isLocked);
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  const isPinModeRef = useRef(isPinMode);
  useEffect(() => {
    isPinModeRef.current = isPinMode;
  }, [isPinMode]);

  const isDraft = drawing.status === "Draft";
  const canDesignAdmin =
    currentMember?.design_admin === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;
  const apsStatusRaw = String(drawing?.aps_status || "")
    .trim()
    .toLowerCase();
  const isApsProcessing =
    String(drawing?.file_type || "").toLowerCase() === "dwg" &&
    !!apsStatusRaw &&
    apsStatusRaw !== "success" &&
    apsStatusRaw !== "failed";
  const apsProgressValue = Number(
    drawing?.aps_progress ??
    drawing?.aps_percentage ??
    drawing?.aps_percent ??
    NaN,
  );

  const siteId = drawing?.site?.id ?? drawing?.site;

  const fileInputRef = useRef(null);

  // --- Autodesk Viewer Logic ---
  useEffect(() => {
    if (!urn || !containerRef.current) return;

    let isCancelled = false;

    const options = {
      env: "AutodeskProduction",
      getAccessToken: async (callback) => {
        const response = await fetch(`${BASE_URL}/design/aps/auth-token/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
        const data = await response.json();
        callback(data.access_token, data.expires_in);
      },
    };

    if (window.Autodesk && window.Autodesk.Viewing) {
      window.Autodesk.Viewing.Initializer(options, () => {
        if (isCancelled || !containerRef.current) return;

        const viewer = new window.Autodesk.Viewing.GuiViewer3D(
          containerRef.current,
          { extensions: ["Autodesk.DocumentBrowser"] }
        );
        viewerRef.current = viewer;
        viewer.start();

        const documentId = `urn:${urn}`;
        window.Autodesk.Viewing.Document.load(documentId, (doc) => {
          if (isCancelled || viewerRef.current !== viewer || !doc) return;

          try {
            // Prefer 3D model if available for isometric view, else fallback to default (usually 2D plan)
            const root = doc.getRoot?.();
            const geometries = root?.search?.({ type: "geometry" }) || [];
            const viewable3D = geometries.find((g) => g?.data?.role === "3d");
            const viewable = viewable3D || root?.getDefaultGeometry?.();

            if (!viewable) return;

            viewer.loadDocumentNode(doc, viewable).then(() => {
              if (!isCancelled && viewerRef.current === viewer) {
                setIsModelLoaded(true);
              }
            });
          } catch (error) {
            console.error("Failed to load APS document:", error);
          }
        });

        // Set cursor for pin mode
        if (isPinMode && viewer.canvas) {
          viewer.canvas.style.cursor = "crosshair";
        }

        // Camera change listener for sticky pins
        const onCameraChange = () => {
          updatePinsScreenPosition(viewer);
        };
        viewer.addEventListener(
          window.Autodesk.Viewing.CAMERA_CHANGE_EVENT,
          onCameraChange,
        );

        // Click handler
        const clickHandler = (e) => handleViewerClick(e, viewer);
        viewer.container.addEventListener("click", clickHandler);

        viewerRef.current._onCameraChange = onCameraChange;
        viewerRef.current._clickHandler = clickHandler;
      });
    }

    return () => {
      isCancelled = true;
      if (viewerRef.current) {
        const v = viewerRef.current;
        if (v._onCameraChange)
          v.removeEventListener(
            window.Autodesk.Viewing.CAMERA_CHANGE_EVENT,
            v._onCameraChange,
          );
        if (v._clickHandler)
          v.container.removeEventListener("click", v._clickHandler);
        v.finish();
        viewerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urn]);

  // Update screen positions whenever pins or camera change
  const updatePinsScreenPosition = (viewer) => {
    if (!viewer) return;
    const currentModelPins = pinsWithModelCoordsRef.current;
    const screenMapped = currentModelPins.map((p) => {
      const screenPos = viewer.worldToClient(
        new window.THREE.Vector3(p.modelX, p.modelY, p.modelZ || 0),
      );
      return { ...p, screenX: screenPos.x, screenY: screenPos.y };
    });
    setPinsWithScreenPos(screenMapped);
  };

  // When pins from DB load or model loads, map them to model coordinates
  useEffect(() => {
    if (!viewerRef.current || !isModelLoaded || !drawing.pins) return;
    const viewer = viewerRef.current;
    const model = viewer.model;
    if (!model) return;

    const bounds = model.getBoundingBox();
    const min = bounds.min;
    const max = bounds.max;

    const mapped = (drawing.pins || []).map((p) => {
      const modelX = min.x + (p.x / 100) * (max.x - min.x);
      const modelY = min.y + (p.y / 100) * (max.y - min.y);
      let modelZ = min.z + (max.z - min.z) / 2;
      if (model.is3d && model.is3d() && typeof p.z !== "undefined") {
        modelZ = min.z + (p.z / 100) * (max.z - min.z);
      }
      return { ...p, modelX, modelY, modelZ };
    });
    setPinsWithModelCoords(mapped);
    pinsWithModelCoordsRef.current = mapped;
  }, [drawing.pins, isModelLoaded, urn]);

  // Initial and subsequent screen position updates
  useEffect(() => {
    if (viewerRef.current) {
      updatePinsScreenPosition(viewerRef.current);
    }
  }, [pinsWithModelCoords]);

  // Cursor management
  useEffect(() => {
    if (viewerRef.current && viewerRef.current.canvas) {
      viewerRef.current.canvas.style.cursor = isPinMode
        ? "crosshair"
        : "default";
    }
  }, [isPinMode]);

  const handleViewerClick = (event, viewer) => {
    if (!isPinModeRef.current || isLockedRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const clientX = event.clientX - rect.left;
    const clientY = event.clientY - rect.top;

    // Try standard hit test first (hits geometry)
    let hitTest = viewer.clientToWorld(clientX, clientY, true);
    let point = hitTest ? hitTest.point : null;

    // Fallback for 2D or background clicks: use clientToWorld from impl which returns a point on the Z=0 plane (usually)
    if (!point) {
      point = viewer.impl.clientToWorld(clientX, clientY);
    }

    if (point) {
      // Map back to percentages for database storage (0-100 relative to full model bounds)
      const bounds = viewer.model.getBoundingBox();
      const min = bounds.min;
      const max = bounds.max;

      const x = ((point.x - min.x) / (max.x - min.x)) * 100;
      const y = ((point.y - min.y) / (max.y - min.y)) * 100;
      const zDelta = max.z - min.z;
      const z = zDelta > 0 ? ((point.z - min.z) / zDelta) * 100 : 0;
      const is3DView = viewer.model?.is3d ? viewer.model.is3d() : false;

      setPendingPin({
        x,
        y,
        z,
        is3DView,
        modelX: point.x,
        modelY: point.y,
        modelZ: point.z,
      });
      setShowPinDialog(true);
    }
  };

  // Load Versions
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showVersionHistory || !drawing?.id) return;
      try {
        setVersionsLoading(true);
        setVersionsError(null);
        const list = await fetchDesignVersions(drawing.id);
        if (!mounted) return;
        setVersions(list);
        // Optional: update parent if needed, but local state is fine for modal
      } catch (err) {
        if (!mounted) return;
        console.error("Fetch version history failed", err);
        setVersionsError(err?.message || "Failed to fetch version history");
        setVersions([]);
      } finally {
        if (mounted) setVersionsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showVersionHistory, drawing.id, refreshTick]);

  // Load Linked Tasks
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      const list = await fetchLinkedTasks(drawing.id);
      if (!mounted) return;
      setLinkedTasks(list);
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  // --- Actions ---

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approveDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Approved" }));
      addAlert("Drawing approved successfully", "success");
    } catch (err) {
      console.error("Approve failed", err);
      addAlert(err?.message || "Failed to approve drawing", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    try {
      setIsLocking(true);
      await lockDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Locked" }));
      setIsPinMode(false);
      addAlert("Drawing locked successfully", "success");
    } catch (err) {
      console.error("Lock failed", err);
      addAlert(err?.message || "Failed to lock drawing", "error");
    } finally {
      setIsLocking(false);
    }
  };

  const handleReplaceClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsReplacing(true);
      const replaced = await replaceDesignFile(drawing.id, file);
      const newDiagramRaw =
        replaced?.diagram ||
        replaced?.fileUrl ||
        replaced?.file_url ||
        drawing.diagram;

      // DWG replacement usually triggers async APS processing.
      // Pull status from APS endpoint so we can reliably show pending/success.
      let apsStatus =
        String(replaced?.aps_status || replaced?.status || "")
          .trim()
          .toLowerCase() || "pending";
      let apsProgress = null;
      try {
        const apsRes = await getDesignApsStatus(drawing.id);
        apsStatus = String(apsRes?.aps_status || apsRes?.status || apsStatus)
          .trim()
          .toLowerCase();
        const rawProgress =
          apsRes?.aps_progress ??
          apsRes?.aps_percentage ??
          apsRes?.aps_percent ??
          apsRes?.progress ??
          apsRes?.percentage ??
          apsRes?.percent;
        const n =
          typeof rawProgress === "number"
            ? rawProgress
            : Number(
              String(rawProgress ?? "")
                .replace("%", "")
                .trim(),
            );
        if (Number.isFinite(n)) {
          apsProgress = Math.max(0, Math.min(100, Math.round(n)));
        }
      } catch (statusErr) {
        console.warn("Failed to fetch APS status after replace", statusErr);
      }

      const updated = {
        ...drawing,
        ...replaced,
        file_type: replaced?.file_type || drawing?.file_type || "dwg",
        diagram: normalizeFileUrl(newDiagramRaw),
        aps_status: apsStatus || "pending",
        ...(apsProgress !== null ? { aps_progress: apsProgress } : {}),
        lastUpdated: new Date().toISOString().split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));

      if (
        updated.file_type?.toLowerCase() === "dwg" &&
        updated.aps_status === "failed"
      ) {
        addAlert(
          "DWG uploaded, but AutoCAD processing failed. Please retry the upload.",
          "error",
        );
      } else if (
        updated.file_type?.toLowerCase() === "dwg" &&
        updated.aps_status !== "success"
      ) {
        addAlert(
          "DWG uploaded. AutoCAD processing may take a few minutes. Returning to dashboard while it finishes.",
          "warning",
        );
        onBack();
      } else {
        addAlert("File replaced successfully.", "success");
      }
    } catch (err) {
      console.error("Replace file failed", err);
      addAlert(err?.message || "Failed to replace file", "error");
    } finally {
      setIsReplacing(false);
    }
  };

  const handleReopenClick = async () => {
    try {
      setIsReopening(true);
      const reopened = await reopenDesign(drawing.id);
      const updated = {
        ...drawing,
        status: "Open",
        lastUpdated: new Date().toISOString().split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      addAlert("Design reopened successfully", "success");
    } catch (err) {
      console.error("Reopen design failed", err);
      addAlert(err?.message || "Failed to reopen design", "error");
    } finally {
      setIsReopening(false);
    }
  };

  const handleConfirmDeleteDesign = async () => {
    addAlert("Are you sure you want to delete this design?", "confirm", {
      onConfirm: async () => {
        try {
          await deleteDesignById(drawing.id);
          if (onDelete) onDelete(drawing.id);
          addAlert("Design deleted", "success");
          onBack();
        } catch (err) {
          console.error("Delete design failed", err);
          addAlert(err?.message || "Failed to delete design", "error");
        }
      },
    });
  };

  // --- Comments & Pins Implementation ---

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      setIsCreatingComment(true);
      const created = await createCommentForDesign(
        drawing.id,
        commentText.trim(),
      );
      const mapped = {
        id: created.id || created.pk || `c${Date.now()}`,
        text: created.comment || created.text || commentText.trim(),
        author: created.created_by || "Unknown",
        createdAt:
          created.created_at || created.createdAt || new Date().toISOString(),
        isResolved: !!created.is_resolved,
      };
      onUpdate((prev) => ({
        ...(prev || drawing),
        comments: [...((prev || drawing).comments || []), mapped],
      }));
      setCommentText("");
    } catch (err) {
      console.error("Create comment failed", err);
      addAlert(err?.message || "Failed to add comment", "error");
    } finally {
      setIsCreatingComment(false);
    }
  };

  const handleCreatePin = async () => {
    if (!pendingPin || !pinComment.trim()) return;
    try {
      setIsCreatingPin(true);
      const payload = {
        x_coordinate: pendingPin.x,
        y_coordinate: pendingPin.y,
        comment: pinComment.trim(),
        assignees: pinAssignees,
      };
      // Only attach z_coordinate if we are pinning in a 3D view
      if (pendingPin.is3DView) {
        payload.z_coordinate = pendingPin.z;
      }
      const created = await createPinForDesign(drawing.id, payload);

      // Normalize created pin for optimistic update
      const mapped = {
        ...created,
        id: created.id || created.pk || `p${Date.now()}`,
        x: payload.x_coordinate,
        y: payload.y_coordinate,
        z: payload.z_coordinate,
        comment: payload.comment,
        status: "pending",
        author: created.created_by || "Me",
        createdAt: created.created_at || new Date().toISOString(),
        assignees: payload.assignees,
      };

      onUpdate((prev) => ({
        ...(prev || drawing),
        pins: [...((prev || drawing).pins || []), mapped],
      }));

      setPinComment("");
      setPinAssignees([]);
      setPendingPin(null);
      setShowPinDialog(false);
      setIsPinMode(false);
      addAlert("Pin added", "success");
    } catch (err) {
      console.error("Create pin failed", err);
      addAlert(err?.message || "Failed to add pin", "error");
    } finally {
      setIsCreatingPin(false);
    }
  };

  const handleResolvePin = (pinId) => {
    setResolvePinId(pinId);
    setShowResolveModal(true);
  };

  const handleConfirmResolve = async () => {
    if (!resolvePinId) return;
    try {
      await resolvePinById(resolvePinId, { comment: resolveComment });
      onUpdate((prev) => ({
        ...(prev || drawing),
        pins: ((prev || drawing).pins || []).map((p) =>
          p.id === resolvePinId ? { ...p, status: "solved" } : p,
        ),
      }));
      setShowResolveModal(false);
      setResolvePinId(null);
      setResolveComment("");
      setActivePinId(null);
      addAlert("Issue marked as resolved", "success");
    } catch (err) {
      console.error("Resolve pin failed", err);
      addAlert(err?.message || "Failed to resolve pin", "error");
    }
  };
  const handleDeletePin = async (pinId) => {
    addAlert("Are you sure you want to delete this pin?", "confirm", {
      onConfirm: async () => {
        try {
          await deletePinById(pinId);
          onUpdate((prev) => ({
            ...(prev || drawing),
            pins: ((prev || drawing).pins || []).filter((p) => p.id !== pinId),
          }));
          setActivePinId(null);
          addAlert("Pin deleted successfully", "success");
        } catch (err) {
          console.error("Delete pin failed", err);
          addAlert(err?.message || "Failed to delete pin", "error");
        }
      },
    });
  };
  const [showAddAssignee, setShowAddAssignee] = useState(false);

  const openIssuesCount = (drawing.pins || []).filter(
    (p) => p.status === "pending",
  ).length;
  const pendingPins = (drawing.pins || []).filter(
    (p) => p.status === "pending",
  );

  const handleViewVersion = (version) => {
    console.log(version);
    if (version.fileType === "pdf") {
      onUpdate((prev) => ({
        ...prev,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
        diagram: version.fileUrl,
        file_type: version.fileType,
      }));
    } else if (
      ["image", "jpeg", "png", "jpg", "svg"].includes(version.fileType)
    ) {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
      }));
    } else if (version.fileType.toLowerCase() === "dwg") {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        urn: version.aps_urn,
        uploaded_by: version.uploadedBy,
      }));
    }
    setShowVersionHistory(false);
  };

  const handleShowCurrentVersion = async () => {
    if (!drawing?.id) return;
    try {
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));

      try {
        const list = await fetchLinkedTasks(drawing.id);
        setLinkedTasks(Array.isArray(list) ? list : []);
      } catch (_) {
        // best-effort; actions can still render correctly from fresh status
      }
    } catch (err) {
      console.error("Failed to refresh current version", err);
      addAlert(err?.message || "Failed to load current version", "error");
    }
  };

  const handleRefreshAll = async () => {
    if (!drawing?.id) return;
    try {
      setIsRefreshing(true);
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));
      setRefreshTick((prev) => prev + 1);
      try {
        await refreshDesignMembers?.();
      } catch (_) {
        // best-effort refresh for members
      }
    } catch (err) {
      console.error("Refresh failed", err);
      addAlert(err?.message || "Failed to refresh design data", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex-1 w-full h-[90vh] flex flex-col p-4 gap-3 bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="px-2 py-1 border rounded cursor-pointer flex items-center gap-2 hover:bg-sm-accent hover:text-white"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Design</span>
              <span>{">"}</span>
              <span className="truncate">{drawing.name}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <h1 className="text-lg font-bold truncate">{drawing.name}</h1>
              <span
                className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}
              >
                {drawing.status}
              </span>
              {isApsProcessing && (
                <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                  AutoCAD Processing
                  {Number.isFinite(apsProgressValue)
                    ? ` ${Math.round(apsProgressValue)}%`
                    : ""}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {drawing.version_number !== drawing.original_version && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
              onClick={() => handleShowCurrentVersion()}
            >
              <History className="w-4 h-4" /> Show Current Version
            </button>
          )}

          <button
            className="flex items-center gap-2 px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50 disabled:opacity-60"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            title="Refresh"
            type="button"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
          {!isLocked && (
            <>
              {canDesignAdmin && (
                <button
                  className={`px-2 py-1 border rounded flex items-center gap-2 bg-white ${isPinMode ? "bg-gray-100 ring-2 ring-purple-500" : ""}`}
                  onClick={() => setIsPinMode((v) => !v)}
                >
                  <Pin className="w-4 h-4" /> {isPinMode ? "Cancel" : "Pin"}
                </button>
              )}
              <button
                className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
                onClick={() => setShowCommentsModal(true)}
              >
                <MessageSquare className="w-4 h-4" /> Comments
              </button>
            </>
          )}
          {canDesignAdmin && (
            <button
              className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
              onClick={handleApprove}
              disabled={
                drawing.status === "Approved" || isLocked || isApproving
              }
            >
              <CheckCircle className="w-4 h-4" />{" "}
              {isApproving
                ? "Approving..."
                : drawing.status === "Approved"
                  ? "Approved"
                  : "Approve"}
            </button>
          )}
          {/* {canDesignAdmin && drawing.status === "Approved" && (
                        <button
                            className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
                            onClick={handleLock}
                            disabled={isLocking}
                        >
                            <Lock className="w-4 h-4" /> {isLocking ? "Locking..." : "Lock"}
                        </button>
                    )} */}
          <button
            className="px-2 py-1 border rounded flex items-center gap-2 bg-white"
            onClick={() => downloadDiagram(drawing.diagram, drawing.name)} // Might need APS specific download?
          >
            <Download className="w-4 h-4" /> Download
          </button>
          {canDesignAdmin && (
            <button
              className="px-2 py-1 text-red-600 flex items-center gap-2"
              onClick={handleConfirmDeleteDesign}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      {/* <div className="grid grid-cols-12 gap-4 flex-1 min-h-0"> */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0 h-full overflow-hidden">
        {/* Viewer Area */}
        {/* <main className="col-span-9 border rounded bg-white min-h-0 relative flex flex-col"> */}
        <main className="col-span-9 border rounded bg-white min-h-0 relative flex flex-col h-full overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center justify-between bg-white z-10">
            <div className="text-sm font-medium">
              Cad Viewer (v{drawing.version_number})
              {drawing.original_version === drawing.version_number && (
                <span className="ml-2 text-xs font-medium bg-sm-secondary/10 px-2 py-1 rounded-lg text-sm-secondary">
                  Latest Version
                </span>
              )}
            </div>
            {/* RIGHT TOOLBAR BUTTONS */}
            <div className="flex items-center gap-2">
              {/* Pending Issues Button */}
              {openIssuesCount > 0 && (
                <button
                  className="flex items-center gap-1 px-2 py-1 text-xs border rounded bg-red-50 text-red-600 hover:bg-red-100"
                  onClick={() => setShowPendingIssuesModal(true)}
                  type="button"
                >
                  <Lock className="w-3 h-3" />
                  {openIssuesCount} Pending Issues
                </button>
              )}

              {/* Version History Button */}

              <button
                className="flex items-center gap-1 px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50"
                onClick={() => setShowVersionHistory(true)}
                type="button"
              >
                <History className="w-3 h-3" />
                Version History
              </button>
              <div className="text-xs text-gray-500">
                {isPinMode
                  ? "Click on the viewer to add a pin"
                  : "Interactive Mode"}
              </div>
            </div>
          </div>
          <div className="flex-1 relative w-full h-full overflow-hidden">
            <div
              ref={containerRef}
              style={{ width: "100%", height: "100%", position: "absolute" }}
            />

            {/* Pins Overlay (Sticky Implementation) */}
            <div className="absolute inset-0 pointer-events-none z-20">
              {(pinsWithScreenPos || []).map((pin) => (
                <div
                  key={pin.id}
                  className="absolute transform -translate-x-1/2 -translate-y-full"
                  style={{ left: `${pin.screenX}px`, top: `${pin.screenY}px` }}
                >
                  <button
                    className="relative pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePinId(activePinId === pin.id ? null : pin.id);
                    }}
                    type="button"
                  >
                    <Pin
                      className={`w-6 h-6 ${pin.status === "solved" ? "text-green-500" : "text-red-500 fill-white"}`}
                    />
                    {activePinId === pin.id && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 z-[100]">
                        <div className="bg-white border p-2 rounded shadow text-left">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <div className="text-xs font-medium text-gray-700">
                                Pin
                              </div>
                              {pin.status === "solved" ? (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">
                                  SOLVED
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">
                                  PENDING
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {pin.status === "pending" &&
                                pin.assignees?.includes(currentMember?.id) && (
                                  <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded">
                                    Assigned to me
                                  </span>
                                )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePinId(null);
                                }}
                                type="button"
                              >
                                <X className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-700 whitespace-pre-wrap mb-1.5">
                            {pin.comment}
                          </p>
                          <div className="text-[10px] text-gray-500 mt-1">
                            {pin.author ? `By: ${pin.author}` : ""}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1">
                            {" "}
                            Assigned To:{" "}
                            {pin.assignees
                              ?.map((assignee) =>
                                mapAssigneeIdToName(assignee, orgMembers),
                              )
                              .join(", ")}
                          </div>
                          {pin.status === "pending" &&
                            (pin.assignees?.includes(currentMember?.id) ||
                              canDesignAdmin) && (
                              <button
                                className="w-full mt-2 p-1 bg-green-600 text-white rounded text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResolvePin(pin.id);
                                }}
                                type="button"
                              >
                                Resolve
                              </button>
                            )}
                          {canDesignAdmin && (
                            <button
                              className="w-full mt-1.5 p-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePin(pin.id);
                              }}
                              type="button"
                            >
                              Delete Pin
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* Right Sidebar */}
        {/* <aside className="col-span-3 flex flex-col gap-3 min-h-0 overflow-auto"> */}
        <aside className="col-span-3 flex flex-col gap-3 h-full overflow-hidden">
          <DesignDetails
            drawing={drawing}
            designMembers={designMembers}
            designMembersLoading={designMembersLoading}
            orgMembersLoading={orgMembersLoading}
            canDesignAdmin={canDesignAdmin}
            setShowAddAssignee={setShowAddAssignee}
          />

          {drawing.status !== "Locked" && (
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500 mb-2">Actions</div>
              <div className="space-y-2">
                {drawing.status !== "Approved" && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60"
                    disabled={isReplacing}
                    onClick={handleReplaceClick}
                  >
                    {isReplacing ? "Replacing..." : "Replace File"}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".dwg" // Restrict to dwg or whatever supports APS
                  className="hidden"
                  onChange={handleReplaceSelected}
                />
              </div>
              <div className="space-y-2 mt-2">
                {(drawing.status === "Approved" ||
                  drawing.status === "Locked") && (
                    <button
                      className="w-full p-2 border rounded disabled:opacity-60"
                      disabled={isReopening || !canDesignAdmin}
                      onClick={handleReopenClick}
                    >
                      {isReopening ? "Re-opening" : "Re-open"}
                    </button>
                  )}
                {drawing.status === "Approved" && canDesignAdmin && (
                  <button
                    className="w-full p-2 border rounded bg-sm-primary text-white hover:bg-sm-primary/90 mt-2"
                    onClick={() => setShowLinkToTask(true)}
                  >
                    {linkedTasks.length > 0
                      ? `${linkedTasks.length} Tasks linked`
                      : "Link to Task"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* {openIssuesCount > 0 && (
            <div className="border rounded p-2 bg-red-50">
              <div className="text-xs text-red-600 font-semibold">
                {openIssuesCount} Pending Issue(s)
              </div>
            </div>
          )} */}

          {/* <button
            className="w-full p-2 border rounded mb-3"
            onClick={() => setShowVersionHistory(true)}
          >
            <History className="w-4 h-4 inline mr-2" /> Version History
          </button> */}

          {/* Comments Sidebar Section */}
          <div className="flex-1 min-h-0 flex flex-col border rounded bg-white overflow-hidden h-0">
            <div className="px-3 py-2 border-b flex items-center justify-between bg-gray-50/50">
              <div className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-600" />
                Comments
              </div>
              <button
                onClick={() => setShowCommentsModal(true)}
                className="text-purple-600 hover:text-purple-700 text-xs font-medium flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto p-2 space-y-2 bg-white"
              style={{ scrollbarWidth: "thin" }}
            >
              {(drawing.comments || [])
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .length === 0 ? (
                <div className="text-[11px] text-gray-400 text-center py-4 italic">
                  No comments yet.
                </div>
              ) : (
                (drawing.comments || [])
                  .slice()
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((c) => (
                    <div
                      key={c.id}
                      className="p-2 border border-gray-100 rounded bg-gray-50/30"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-gray-900 truncate max-w-[100px]">
                          {c.author}
                        </span>
                        <div className="flex items-center gap-2">
                          {c.pin_linked && (
                            <span
                              onClick={() => setActivePinId(c.pin_linked)}
                              className="text-sm-primary text-[10px] cursor-pointer bg-sm-primary/10 px-2 py-0.5 rounded "
                            >
                              Linked to Pin #{c.pin_linked}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400">
                            {c.createdAt
                              ? new Date(c.createdAt).toLocaleDateString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 leading-relaxed break-words">
                        {c.text}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Modals */}
      {/* Comments modal */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-2xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Comments</h3>
              <button onClick={() => setShowCommentsModal(false)} type="button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="max-h-80 overflow-auto border rounded p-2 bg-gray-50">
              {(drawing.comments || []).length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6">
                  No comments yet
                </div>
              ) : (
                <div className="space-y-2">
                  {(drawing.comments || []).map((c) => (
                    <div key={c.id} className="bg-white border rounded p-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate">
                            {c.author}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
                            {c.text}
                          </div>
                        </div>
                        <div className="text-[10px] text-gray-400 flex flex-row items-center gap-2">
                          {c.pin_linked && (
                            <span className="text-sm-primary bg-sm-primary/10 px-2 py-0.5 rounded ">
                              Linked to Pin #{c.pin_linked}
                            </span>
                          )}
                          {c.createdAt
                            ? new Date(c.createdAt).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="flex-1 border py-2 px-3 rounded text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                disabled={isCreatingComment}
              />
              <button
                className="px-3 py-2 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleAddComment}
                disabled={isCreatingComment || !commentText.trim()}
              >
                {isCreatingComment ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Assignee Modal */}
      {/* Design Member Selector Modal */}
      <DesignMemberSelector
        show={showAddAssignee}
        onClose={() => setShowAddAssignee(false)}
        designId={drawing.id}
        orgMembers={orgMembers}
        orgMembersLoading={orgMembersLoading}
        currentDesignMembers={designMembers}
        onUpdate={refreshDesignMembers}
      />

      {/* Pin Dialog */}
      {showPinDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <h3 className="font-semibold mb-2">Add Comment Pin</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Comment
                </label>
                <textarea
                  placeholder="Enter your comment or issue description..."
                  value={pinComment}
                  onChange={(e) => setPinComment(e.target.value)}
                  rows={3}
                  className="w-full border p-2 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Assignees
                </label>
                <div className="border rounded p-2 max-h-32 overflow-y-auto space-y-1">
                  {(designMembers || []).map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={pinAssignees.includes(m.member_id || m.id)}
                        onChange={(e) => {
                          const id = m.member_id || m.id;
                          if (e.target.checked) {
                            setPinAssignees((prev) => [...prev, id]);
                          } else {
                            setPinAssignees((prev) =>
                              prev.filter((pid) => pid !== id),
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs">
                        {displayNameFromOrgMember(m)}
                      </span>
                    </label>
                  ))}
                  {(designMembers || []).length === 0 && (
                    <span className="text-xs text-gray-400">
                      No members to assign
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowPinDialog(false);
                  setPendingPin(null);
                  setPinComment("");
                  setPinAssignees([]);
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleCreatePin}
                disabled={!pinComment.trim() || isCreatingPin}
              >
                {isCreatingPin ? "Adding..." : "Add Pin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Pin Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]">
          <div className="bg-white p-4 rounded w-full max-w-md shadow-xl">
            <h3 className="font-semibold mb-2">Resolve Pin</h3>
            <p className="text-xs text-gray-500 mb-2">
              Optional: add a comment about how this was resolved.
            </p>
            <textarea
              placeholder="Resolution notes..."
              value={resolveComment}
              onChange={(e) => setResolveComment(e.target.value)}
              rows={3}
              className="w-full border p-2 rounded text-sm mb-3"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowResolveModal(false);
                  setResolvePinId(null);
                  setResolveComment("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                onClick={handleConfirmResolve}
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {showPendingIssuesModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[210]">
          <div className="bg-white p-4 rounded w-full max-w-2xl shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Pending Pins</h3>
              <button
                type="button"
                onClick={() => setShowPendingIssuesModal(false)}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-auto border rounded bg-gray-50 p-2">
              {pendingPins.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6">
                  No pending pins found.
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingPins.map((pin, idx) => (
                    <button
                      key={pin.id || idx}
                      type="button"
                      className="w-full text-left bg-white border rounded p-2 hover:bg-gray-50"
                      onClick={() => {
                        setActivePinId(pin.id);
                        setShowPendingIssuesModal(false);
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-red-600">
                          Pin #{pin.id || idx + 1}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {pin.createdAt
                            ? new Date(pin.createdAt).toLocaleDateString()
                            : ""}
                        </span>
                      </div>
                      <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">
                        {pin.comment || "No comment"}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {pin.author ? `By: ${pin.author}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-3">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => setShowPendingIssuesModal(false)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Version History</h3>
              <button onClick={() => setShowVersionHistory(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-auto">
              {versionsLoading && (
                <div className="text-sm text-gray-500 p-3">
                  Loading versions...
                </div>
              )}
              {!versionsLoading && versionsError && (
                <div className="text-sm text-red-600 p-3">{versionsError}</div>
              )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).length === 0 && (
                  <div className="text-sm text-gray-500 p-3">
                    No versions found.
                  </div>
                )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xs font-bold bg-gray-200 px-2 py-1 rounded shrink-0">
                        {version.version}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{version.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {version.uploadedBy || drawing.uploadedBy} •{" "}
                          {version.uploadDate || drawing.lastUpdated}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(version.status)}`}
                      >
                        {version.status}
                      </span>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-sm-accent hover:text-white"
                        disabled={!version.fileUrl}
                        onClick={() => handleViewVersion(version)}
                      >
                        View
                      </button>
                      <button
                        className="px-2 py-1 text-sm border rounded disabled:opacity-60"
                        disabled={!version.fileUrl}
                        onClick={() =>
                          downloadDiagram(
                            version.fileUrl,
                            `${drawing.name}_${version.version}`,
                          )
                        }
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      <DesignTaskLinkSelector
        show={showLinkToTask}
        onClose={() => setShowLinkToTask(false)}
        designId={drawing.id}
        siteId={siteId}
        onLinked={onUpdate}
      />
    </div>
  );
};
